import { keys } from 'lodash';

import { DocumentType } from '@/types/schema';
import { pluralize } from '@/utils/formatting/strings';

import { DOCUMENT_TYPE_DISPLAY_NAMES } from '../../documents.constants';

// Core configuration - single source of truth for document relationships
const DOCUMENT_RELATIONSHIPS: Record<string, DocumentType[]> = {
  [DocumentType.SignedTrustDocument]: [
    DocumentType.SignedTrustDocument,
    DocumentType.TrustAmendment,
    DocumentType.TrustRelatedDocument,
  ],
  [DocumentType.Will]: [DocumentType.Will, DocumentType.WillCodicil],
  [DocumentType.LlcAgreement]: [
    DocumentType.LlcAgreement,
    DocumentType.LlcAgreementAmendment,
  ],
  [DocumentType.OperatingAgreement]: [
    DocumentType.OperatingAgreement,
    DocumentType.OperatingAgreementAmendment,
  ],
  [DocumentType.PartnershipAgreement]: [
    DocumentType.PartnershipAgreement,
    DocumentType.PartnershipAgreementAmendment,
  ],
};

// Build a flattened map once at module-load time
type RelationshipInfo = {
  primary: DocumentType;
  compatible: DocumentType[];
};

const RELATIONSHIP_LOOKUP: Partial<Record<DocumentType, RelationshipInfo>> =
  Object.entries(DOCUMENT_RELATIONSHIPS).reduce(
    (acc, [primary, list]) => {
      list.forEach((dt: DocumentType) => {
        acc[dt] = {
          primary: primary as DocumentType,
          compatible: list,
        };
      });
      return acc;
    },
    {} as Partial<Record<DocumentType, RelationshipInfo>>
  );

// Export for external use if needed
export { RELATIONSHIP_LOOKUP as DOCUMENT_RELATIONSHIP_LOOKUP };

export interface SelectableDocument {
  id: string;
  name: string;
  type: DocumentType;
}

export interface DocumentGroup {
  label: string;
  docs: SelectableDocument[];
}

export function getCompatibleDocumentTypes(
  currentDocumentType: DocumentType
): DocumentType[] {
  return Array.from(RELATIONSHIP_LOOKUP[currentDocumentType]?.compatible ?? []);
}

/**
 * Groups documents by their individual document types
 * Each document type gets its own section
 */
export function groupDocumentsByCompatibleTypes(
  documents: SelectableDocument[]
): Record<string, DocumentGroup> {
  const groupMap = new Map<DocumentType, SelectableDocument[]>();

  // Group documents by their individual type
  documents.forEach((doc) => {
    if (!groupMap.has(doc.type)) {
      groupMap.set(doc.type, []);
    }
    groupMap.get(doc.type)!.push(doc);
  });

  // Convert map to groups object
  const result: Record<string, DocumentGroup> = {};

  groupMap.forEach((docs, documentType) => {
    result[documentType] = {
      label: pluralize({
        count: 2, // we always want it to be plural because it's a group name
        singular: DOCUMENT_TYPE_DISPLAY_NAMES[documentType],
        includeCount: false,
      }),
      docs,
    };
  });

  return result;
}

/**
 * Gets all document types that are eligible for version management
 * These are document types that have compatible document types (can be grouped together)
 */
export function getEligibleDocumentTypes(): DocumentType[] {
  return keys(RELATIONSHIP_LOOKUP) as DocumentType[];
}

export function isWillDocumentType(
  documentType: DocumentType | undefined
): boolean {
  return (
    documentType === DocumentType.Will ||
    documentType === DocumentType.WillCodicil
  );
}

export interface CalloutInfo {
  shouldShowCallout: boolean;
  primaryDocumentTypeName: string;
}

export interface DocumentWithType {
  type: DocumentType;
}

export function hasDocumentOrderChanged(
  currentDocuments: Array<{ id: string }>,
  nextDocuments: Array<{ id: string }>
): boolean {
  return (
    currentDocuments.length !== nextDocuments.length ||
    currentDocuments.some(
      (document, index) => document.id !== nextDocuments[index]?.id
    )
  );
}

export function willVersionChangeGenerateCombinedSummary(
  currentDocuments: Array<{ id: string }>,
  nextDocuments: Array<{ id: string }>
): boolean {
  return (
    nextDocuments.length > 1 &&
    hasDocumentOrderChanged(currentDocuments, nextDocuments)
  );
}

/**
 * Determines whether to show the callout message and returns the primary document type name
 * when there are multiple documents in the same relationship group.
 *
 * @param relatedDocuments - Array of documents that can be amendments/versions of each other
 * @param currentDocumentType - The type of the current document being managed
 * @returns Object containing shouldShowCallout boolean and primaryDocumentTypeName string
 */
export function getCalloutInfo(
  relatedDocuments: DocumentWithType[],
  currentDocumentType: DocumentType
): CalloutInfo {
  // Don't show callout if there's only one document or none
  if (relatedDocuments.length <= 1) {
    return { shouldShowCallout: false, primaryDocumentTypeName: '' };
  }

  const relationshipInfo = RELATIONSHIP_LOOKUP[currentDocumentType];
  if (!relationshipInfo) {
    return { shouldShowCallout: false, primaryDocumentTypeName: '' };
  }

  // Count only the primary documents (not amendments)
  const primaryDocuments = relatedDocuments.filter(
    (doc) => doc.type === relationshipInfo.primary
  );

  const shouldShowCallout = primaryDocuments.length >= 2;
  const primaryDocumentTypeName = shouldShowCallout
    ? DOCUMENT_TYPE_DISPLAY_NAMES[relationshipInfo.primary]
    : '';

  return {
    shouldShowCallout,
    primaryDocumentTypeName,
  };
}
